import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { SharedModule } from '@aiwyn/shared';
import { CxpEsignSignatureState, CxpEsignSigner } from '../../../../models';
import { getCxpContactInitials } from '../../../../shared';

/**
 * The signature card and signer roster on the eSign task detail page
 * (Figma 2.0 / 2.1 / 3.0 / 3.1).
 *
 * Presentational: every state comes in as an input, so the three variants the
 * design defines — signature required, signed and waiting on co-signers, and
 * fully complete — are reachable in a test without a backend.
 */
@Component({
  selector: 'cxp-esign-signature-block',
  templateUrl: './esign-signature-block.component.html',
  styleUrls: ['./esign-signature-block.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SharedModule],
})
export class EsignSignatureBlockComponent {
  readonly state = input<CxpEsignSignatureState>('required');
  readonly filename = input<string | null>(null);
  readonly signedAt = input<string | null>(null);
  readonly signedFileUrl = input<string | null>(null);
  readonly signers = input<CxpEsignSigner[]>([]);

  readonly reviewAndSign = output<void>();
  readonly viewDocument = output<void>();

  protected readonly isCompleted = computed(() => this.state() === 'completed');

  /** Shown on the disabled download while the signed PDF is still being assembled. */
  protected readonly downloadPendingMessage = 'Your signed copy is still being prepared and is not ready to download yet.';

  protected readonly statusTitle = computed(() => (this.isCompleted() ? 'Signature Completed' : 'Signature Required'));

  /**
   * The document name leads both subtitles, so a bundle whose filename has not
   * resolved falls back to "This document" rather than rendering a dangling
   * "requires your signature" with nothing in front of it.
   */
  protected readonly statusSubtitle = computed(() => {
    const document = this.filename() ?? 'This document';
    if (!this.isCompleted()) return `${document} requires your signature.`;

    const signedOn = this.formattedSignedAt();
    const signed = signedOn ? `${document} has been signed on ${signedOn}.` : `${document} has been signed.`;
    return `${signed} A copy has been sent to your email.`;
  });

  private readonly formattedSignedAt = computed(() => {
    const raw = this.signedAt();
    if (!raw) return null;
    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime())
      ? null
      : parsed.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  });

  initialsFor(signer: CxpEsignSigner): string {
    return getCxpContactInitials(signer.displayName, signer.email ?? '');
  }
}
